import React, { useState } from "react";
import { Sidebar } from "../components/Sidebar";
import {
  Search,
  Settings,
  Bell,
  Pause,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Filter,
  Calendar,
  Zap,
  Github,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Globe,
  Menu,
} from "lucide-react";
import { Button } from "../components/ui/button";

import { Badge } from "../components/ui/badge";
import { useNavigate } from "@tanstack/react-router";
import { cn } from "../lib/utils";

// Removed navigation props since TanStack Router handles navigation

interface TestRun {
  id: string;
  name: string;
  testSuite: string;
  agent: string;
  status: "running" | "completed" | "failed" | "scheduled" | "cancelled";
  startTime: Date;
  endTime?: Date;
  duration?: string;
  progress: number;
  totalTests: number;
  passedTests: number;
  failedTests: number;
  anomalies: number;
  trigger: "manual" | "scheduled" | "webhook";
  source: "github_pr" | "manual" | "api" | "scheduled" | "webhook";
  sourceDetails?: {
    prNumber?: string;
    branch?: string;
    commit?: string;
    repository?: string;
  };
}

// Utility to format time difference as 'X minutes ago', 'Scheduled for X from now', etc.
function formatRelativeTime(date: Date, now: Date) {
  const diff = date.getTime() - now.getTime();
  const absDiff = Math.abs(diff);
  const isFuture = diff > 0;
  if (absDiff < 60 * 1000)
    return isFuture ? "Scheduled for <1 minute from now" : "<1 minute ago";
  if (absDiff < 60 * 60 * 1000) {
    const mins = Math.round(absDiff / (60 * 1000));
    return isFuture
      ? `Scheduled for ${mins} minute${mins > 1 ? "s" : ""} from now`
      : `${mins} minute${mins > 1 ? "s" : ""} ago`;
  }
  if (absDiff < 24 * 60 * 60 * 1000) {
    const hours = Math.round(absDiff / (60 * 60 * 1000));
    return isFuture
      ? `Scheduled for ${hours} hour${hours > 1 ? "s" : ""} from now`
      : `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }
  const days = Math.round(absDiff / (24 * 60 * 60 * 1000));
  return isFuture
    ? `Scheduled for ${days} day${days > 1 ? "s" : ""} from now`
    : `${days} day${days > 1 ? "s" : ""} ago`;
}

// Utility to format duration as 'X minutes', 'X hours Y minutes', etc.
function formatDuration(ms: number) {
  if (ms < 60 * 1000) return "<1 minute";
  const mins = Math.floor(ms / (60 * 1000));
  const hours = Math.floor(mins / 60);
  if (hours > 0) {
    return `${hours} hour${hours > 1 ? "s" : ""} ${mins % 60} minute${mins % 60 !== 1 ? "s" : ""}`;
  }
  return `${mins} minute${mins !== 1 ? "s" : ""}`;
}

// --- Update sampleTestRuns to use Date objects ---
const now = new Date();
const minutesAgo = (n: number) => new Date(now.getTime() - n * 60 * 1000);
const hoursAgo = (n: number) => new Date(now.getTime() - n * 60 * 60 * 1000);
const minutesFromNow = (n: number) => new Date(now.getTime() + n * 60 * 1000);
const hoursFromNow = (n: number) =>
  new Date(now.getTime() + n * 60 * 60 * 1000);

const sampleTestRuns: TestRun[] = [
  {
    id: "1",
    name: "Conversation Flow Test Run #1247",
    testSuite: "Conversation Flow Tests",
    agent: "Beauty Sales Agent",
    status: "running",
    startTime: minutesAgo(2),
    progress: 75,
    totalTests: 156,
    passedTests: 117,
    failedTests: 0,
    anomalies: 2,
    trigger: "scheduled",
    source: "github_pr",
    sourceDetails: {
      prNumber: "PR #1234",
      branch: "feature/new-conversation-flow",
      commit: "abc123",
      repository: "rippletide/ai-testing",
    },
  },
];

// Update TestRun interface to use Date for startTime/endTime
declare module "react" {
  interface TestRun {
    startTime: Date;
    endTime?: Date;
    // ...rest unchanged
  }
}

// Update usages in TestRunRow and page rendering
// In TestRunRow, replace usages of testRun.startTime and testRun.endTime with formatted strings
// Calculate duration as needed

const TestRunRow: React.FC<{ testRun: TestRun }> = ({ testRun }) => {
  const navigate = useNavigate();
  const now = new Date();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "bg-blue-900/30 text-blue-400 border-blue-600/30";
      case "completed":
        return "bg-green-900/30 text-green-400 border-green-600/30";
      case "failed":
        return "bg-red-900/30 text-red-400 border-red-600/30";
      case "scheduled":
        return "bg-yellow-900/30 text-yellow-400 border-yellow-600/30";
      case "cancelled":
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
      default:
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "running":
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case "completed":
        return <CheckCircle className="h-4 w-4" />;
      case "failed":
        return <XCircle className="h-4 w-4" />;
      case "scheduled":
        return <Clock className="h-4 w-4" />;
      case "cancelled":
        return <XCircle className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case "github_pr":
        return <Github className="h-3 w-3" />;
      case "manual":
        return <Zap className="h-3 w-3" />;
      case "api":
        return <Globe className="h-3 w-3" />;
      case "scheduled":
        return <Calendar className="h-3 w-3" />;
      case "webhook":
        return <Zap className="h-3 w-3" />;
      default:
        return <Zap className="h-3 w-3" />;
    }
  };

  const getSourceLabel = (source: string, details?: any) => {
    switch (source) {
      case "github_pr":
        return details?.prNumber ? `PR #${details.prNumber}` : "GitHub PR";
      case "manual":
        return "Manual";
      case "api":
        return "API";
      case "scheduled":
        return "Scheduled";
      case "webhook":
        return "Webhook";
      default:
        return "Unknown";
    }
  };

  const formatRelativeTime = (date: Date, now: Date) => {
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) {
      return `${diffInSeconds}s ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    }
  };

  return (
    <div
      className="flex items-center justify-between p-4 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-750 transition-colors cursor-pointer"
      onClick={() =>
        navigate({
          to: "/test-runs/$testRunId",
          params: { testRunId: testRun.id },
        })
      }
    >
      <div className="flex items-center space-x-4 flex-1">
        {/* Name and Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-medium text-gray-100 truncate">
              {testRun.name}
            </h3>
            <div className="flex items-center space-x-1 text-xs text-gray-400">
              <span>{testRun.testSuite}</span>
              <span>•</span>
              <span>{testRun.agent}</span>
            </div>
          </div>
        </div>

        {/* Source */}
        <div className="flex items-center space-x-1 text-xs text-gray-400">
          {getSourceIcon(testRun.source)}
          <span>{getSourceLabel(testRun.source, testRun.sourceDetails)}</span>
        </div>

        {/* Test Results */}
        <div className="flex items-center space-x-2 text-xs">
          <Badge
            variant="secondary"
            className="bg-pink-900/30 text-pink-400 border-pink-600/30"
          >
            {testRun.passedTests} passed
          </Badge>
          {testRun.failedTests > 0 && (
            <Badge
              variant="destructive"
              className="bg-red-900/30 text-red-400 border-red-600/30"
            >
              {testRun.failedTests} failed
            </Badge>
          )}
          {testRun.anomalies > 0 && (
            <Badge variant="outline" className="border-gray-600 text-gray-300">
              {testRun.anomalies} anomalies
            </Badge>
          )}
        </div>

        {/* Time */}
        <div className="text-xs text-gray-400 min-w-0">
          {formatRelativeTime(testRun.startTime, now)}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          {testRun.status === "running" && (
            <Button
              size="sm"
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
              onClick={(e) => {
                e.stopPropagation();
                // Handle pause action
              }}
            >
              <Pause className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const TestRunsPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const itemsPerPage = 10;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const now = Date.now();

  // Filter test runs based on current filters
  let filteredTestRuns = sampleTestRuns.filter((testRun) => {
    const matchesStatus =
      statusFilter === "all" || testRun.status === statusFilter;
    const matchesSource =
      sourceFilter === "all" || testRun.source === sourceFilter;
    const matchesSearch =
      searchQuery === "" ||
      testRun.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      testRun.testSuite.toLowerCase().includes(searchQuery.toLowerCase()) ||
      testRun.agent.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesStatus && matchesSource && matchesSearch;
  });

  // Sort by running date (most recent first, including scheduled)
  filteredTestRuns = filteredTestRuns.sort(
    (a, b) => b.startTime.getTime() - a.startTime.getTime(),
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredTestRuns.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTestRuns = filteredTestRuns.slice(startIndex, endIndex);

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed lg:relative z-50 h-full transition-transform duration-300 ease-in-out",
          isSidebarOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
        )}
      >
        <Sidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-gray-300 hover:bg-gray-700"
                onClick={toggleSidebar}
              >
                <Menu className="h-5 w-5" />
              </Button>

              <h1 className="text-xl lg:text-2xl font-bold text-gray-100">
                Test Runs
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="default"
                size="icon"
                className="bg-gray-700 hover:bg-gray-600 text-gray-300"
              >
                <Bell className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-300 hover:bg-gray-700"
              >
                <Settings className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Filters */}
        <div className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-3">
          <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-300">Filters:</span>
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-md text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="all">All Status</option>
                <option value="running">Running</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="scheduled">Scheduled</option>
                <option value="cancelled">Cancelled</option>
              </select>

              {/* Source Filter */}
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-md text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="all">All Sources</option>
                <option value="github_pr">GitHub PR</option>
                <option value="manual">Manual</option>
                <option value="api">API</option>
                <option value="scheduled">Scheduled</option>
                <option value="webhook">Webhook</option>
              </select>
            </div>

            <span className="text-sm text-gray-400">
              {filteredTestRuns.length} of {sampleTestRuns.length} runs
            </span>
            <div className="relative lg:ml-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-1 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-gray-100 placeholder-gray-400 w-full lg:w-auto"
              />
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <main className="flex-1 h-full flex flex-col min-h-0 bg-gray-900">
          {/* Test Runs List with Pagination */}
          <div className="h-full flex flex-col min-h-0">
            {/* Scrollable content area */}
            <div className="flex-1 p-4 lg:p-6 overflow-auto min-h-0">
              <div className="space-y-4">
                {currentTestRuns.map((testRun) => (
                  <TestRunRow key={testRun.id} testRun={testRun} />
                ))}
              </div>
            </div>

            {/* Pagination - Always visible at bottom */}
            {totalPages > 1 && (
              <div className="border-t border-gray-700 bg-gray-800 px-4 lg:px-6 py-4 flex-shrink-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                  <div className="text-sm text-gray-400">
                    Showing {startIndex + 1} to{" "}
                    {Math.min(endIndex, filteredTestRuns.length)} of{" "}
                    {filteredTestRuns.length} results
                  </div>

                  <div className="flex items-center justify-center sm:justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <Button
                            key={page}
                            variant={
                              page === currentPage ? "default" : "outline"
                            }
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className={
                              page === currentPage
                                ? "bg-pink-400 hover:bg-pink-500 text-white"
                                : "border-gray-600 text-gray-300 hover:bg-gray-700"
                            }
                          >
                            {page}
                          </Button>
                        ),
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};
